import React, { useEffect, useState } from "react";
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Card,
  CardBody,
  Spinner,
  Form,
  Container,
  FormFeedback,
  CardHeader,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import BlogService from "../../../services/KangarooServices/BlogService";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MediaService from "../../../services/KangarooServices/MediaServices";
import MediaModal from "../../PagesSection/Component/MediaModal";
import { imageBaseApi } from "../../../utilis/imageHelper";
import Flatpickr from "react-flatpickr";

const BlogCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initialData, setInitialData] = useState();
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaData, setMediaData] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [activeMediaType, setActiveMediaType] = useState(null); // banner | normal
  const [bannerMedia, setBannerMedia] = useState(null);
  const [normalMedia, setNormalMedia] = useState(null);

  const toggleMedia = (type = null) => {
    setActiveMediaType(type);
    setIsMediaOpen((p) => !p);
  };

  const statusMap = {
    Draft: 1,
    Published: 2,
    Archived: 3,
  };

  /* ---------------- FETCH BLOG DETAIL ---------------- */
  const fetchDetail = async () => {
    try {
      const response = await BlogService.getBySlug(id);

      // Convert string status to enum
      const numericStatus = statusMap[response.status] || 1;

      setInitialData({
        ...response,
        status: numericStatus,
      });

      setBannerMedia(response?.coverMedia || null);
      setNormalMedia(response?.thumbnailMedia || null);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) fetchDetail();
  }, [id]);

  /* ---------------- FETCH MEDIA ---------------- */
  const fetchMedia = async (page = 1, size = 1000) => {
    try {
      const response = await MediaService.getList({ page, pageSize: size });
      setMediaData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  /* ---------------- FORMIK ---------------- */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: initialData?.id || "",
      title: initialData?.title || "",
      shortDescription: initialData?.shortDescription || "",
      content: initialData?.content || "",
      readingTime: initialData?.readingTime || "",
      status: initialData?.status || 1,
      metaTitle: initialData?.metaTitle || "",
      metaDescription: initialData?.metaDescription || "",
      isFeatured: initialData?.isFeatured || false,
      publishedAt: initialData?.publishedAt
        ? new Date(initialData.publishedAt)
        : null,
    },

    onSubmit: async (values) => {
      await formik.validateForm();

      if (Object.keys(formik.errors).length) {
        formik.setTouched(
          Object.keys(formik.initialValues).reduce((a, c) => {
            a[c] = true;
            return a;
          }, {})
        );
        return;
      }

      try {
        const payload = {
          title: values.title,
          shortDescription: values.shortDescription,
          content: values.content,
          readingTime: Number(values.readingTime),
          metaTitle: values.metaTitle,
          metaDescription: values.metaDescription,
          status: Number(values.status), // ✅ numeric enum
          coverMediaId: bannerMedia?.id || null,
          thumbnailMediaId: normalMedia?.id || null,
          publishedAt:
            values.status === 2 && values.publishedAt instanceof Date
              ? values.publishedAt.toISOString()
              : null,
        };

        if (values?.id) {
          await BlogService.update(values?.id, payload);
          toast.success("Update successful");
        } else {
          await BlogService.create(payload);
          toast.success("Create successful");
        }

        navigate("/blog");
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong");
      }
    },
  });

  /* ---------------- UI ---------------- */
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Blog Form"
          pageTitle="Blog"
          breadcrumbItems={[{ title: "Back to List", link: "/blog" }]}
        />

        <form onSubmit={formik.handleSubmit}>
          {/* Submit Buttons */}
          <div className="d-flex justify-content-end mb-3 gap-3">
            <button type="submit" className="btn btn-success">
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/blog")}
            >
              Cancel
            </button>
          </div>

          <Row>
            {/* LEFT */}
            <Col lg="8">
              {/* Blog Info */}
              <Card>
                <CardBody>
                  <FormGroup>
                    <Label>
                      Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.title && !!formik.errors.title}
                    />
                    <FormFeedback>{formik.errors.title}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Short Description</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      name="shortDescription"
                      value={formik.values.shortDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.shortDescription &&
                        !!formik.errors.shortDescription
                      }
                    />
                    <FormFeedback>
                      {formik.errors.shortDescription}
                    </FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Content</Label>
                    <ReactQuill
                      theme="snow"
                      value={formik.values.content}
                      onChange={(v) => formik.setFieldValue("content", v)}
                      onBlur={() => formik.setFieldTouched("content", true)}
                    />
                    {formik.touched.content && formik.errors.content && (
                      <div className="text-danger mt-1">
                        {formik.errors.content}
                      </div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>

              {/* Cover Media */}
              <Card>
                <CardBody>
                  <Label>Cover Media</Label>
                  {bannerMedia ? (
                    <div className="text-center p-3 border rounded shadow-sm bg-light">
                      <div
                        style={{
                          maxHeight: "220px",
                          overflow: "hidden",
                          borderRadius: "10px",
                          border: "1px solid #ddd",
                        }}
                        className="mb-3"
                      >
                        <img
                          src={`${imageBaseApi}${bannerMedia.url}`}
                          alt="Banner"
                          style={{ width: "100%", objectFit: "cover" }}
                          className="img-fluid hover-zoom"
                        />
                      </div>
                      <div className="mb-2">
                        <div className="fw-semibold">
                          {bannerMedia.fileName || "Banner Image"}
                        </div>
                        {bannerMedia.size && (
                          <small className="text-muted">
                            Size: {(bannerMedia.size / 1024).toFixed(1)} KB
                          </small>
                        )}
                      </div>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          className="rounded-pill px-3"
                          onClick={() => toggleMedia("banner")}
                        >
                          Change
                        </Button>
                        <Button
                          size="sm"
                          outline
                          color="danger"
                          className="rounded-pill px-3"
                          onClick={() => setBannerMedia(null)}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i> Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded text-center bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className=" text-muted">No banner image selected</h6>
                      <h6 className="mb-3 text-muted">1280 x 720</h6>
                      <Button
                        outline
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={() => toggleMedia("banner")}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose Banner
                        Image
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* RIGHT */}
            <Col lg="4">
              {/* Thumbnail Media */}
              <Card>
                <CardBody>
                  <Label>Thumbnail Media</Label>
                  {normalMedia ? (
                    <div className="d-flex flex-column align-items-center gap-3 p-3 border rounded shadow-sm bg-light">
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #ddd",
                        }}
                      >
                        <img
                          src={`${imageBaseApi}${normalMedia.url}`}
                          alt="media"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          className="hover-zoom"
                        />
                      </div>
                      <div className="d-flex flex-column justify-content-between">
                        <div>
                          <div className="fw-semibold">
                            {normalMedia.fileName || "Media File"}
                          </div>
                          {normalMedia.size && (
                            <small className="text-muted">
                              Size: {(normalMedia.size / 1024)?.toFixed(1)} KB
                            </small>
                          )}
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <Button
                            size="sm"
                            color="primary"
                            outline
                            className="rounded-pill px-3"
                            onClick={() => toggleMedia("normal")}
                          >
                            Change
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            type="button"
                            className="rounded-pill px-3"
                            onClick={() => setNormalMedia(null)}
                          >
                            <i className="ri-delete-bin-5-line me-1"></i> Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-4 border rounded bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-2 text-muted">No media added</h6>
                      <h6 className="mb-3 text-muted">1200 x 630</h6>
                      <Button
                        outline
                        size="sm"
                        color="secondary"
                        className="rounded-pill px-3"
                        onClick={() => toggleMedia("normal")}
                      >
                        <i className="ri-image-add-line me-1"></i> Add Media
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Other Details */}
              <Card>
                <CardBody>
                  <FormGroup className="d-flex justify-content-between">
                    <Label>Is Featured?</Label>
                    <Input
                      type="checkbox"
                      checked={formik.values.isFeatured}
                      onChange={(e) =>
                        formik.setFieldValue("isFeatured", e.target.checked)
                      }
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Reading Time (minutes)</Label>
                    <Input
                      type="number"
                      name="readingTime"
                      value={formik.values.readingTime}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.readingTime &&
                        !!formik.errors.readingTime
                      }
                    />
                    <FormFeedback>{formik.errors.readingTime}</FormFeedback>
                  </FormGroup>
                  <FormGroup>
                    <Label>
                      Status <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="status"
                      value={formik.values.status}
                      onChange={(e) => {
                        const status = Number(e.target.value);
                        formik.setFieldValue("status", status);

                        if (status === 2 && !formik.values.publishedAt) {
                          formik.setFieldValue("publishedAt", new Date());
                        }
                        if (status !== 2) {
                          formik.setFieldValue("publishedAt", null);
                        }
                      }}
                    >
                      <option value="1">Draft</option>
                      <option value="2">Published</option>
                      <option value="3">Archived</option>
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Published Time</Label>
                    <Flatpickr
                      disabled={formik.values.status !== 2}
                      className="form-control"
                      value={formik.values.publishedAt}
                      onChange={(dates) =>
                        formik.setFieldValue("publishedAt", dates?.[0] || null)
                      }
                      options={{ enableTime: true, dateFormat: "Y-m-d H:i" }}
                    />
                  </FormGroup>
                </CardBody>
              </Card>

              {/* Meta Info */}
              <Card>
                <CardHeader>Meta Information</CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label>
                      Meta Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="metaTitle"
                      value={formik.values.metaTitle}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>
                      Meta Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows="2"
                      name="metaDescription"
                      value={formik.values.metaDescription}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </div>

      <MediaModal
        isOpen={isMediaOpen}
        toggle={() => setIsMediaOpen(false)}
        mediaData={mediaData}
        selectedMedia={activeMediaType === "banner" ? bannerMedia : normalMedia}
        setSelectedMedia={(media) => {
          if (activeMediaType === "banner") setBannerMedia(media);
          else if (activeMediaType === "normal") setNormalMedia(media);
        }}
      />
    </Container>
  );
};

export default BlogCreate;
